import {useCallback, useEffect, useState} from 'react';

import {businessUpdateService} from '@/services/businessUpdateService';
import type {AdminBusinessUpdateListItem, BusinessUpdateStatus} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

const useProductUpdatesList = () => {
  const [list, setList] = useState<AdminBusinessUpdateListItem[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [status, setStatus] = useState<BusinessUpdateStatus | undefined>(
    undefined,
  );
  // Derived from which query last finished, so no fetch has to set it up front.
  const queryKey = `${page}:${status ?? ''}`;
  const [loadedQueryKey, setLoadedQueryKey] = useState<string>();
  const isLoading = loadedQueryKey !== queryKey;

  // State is only set in the promise callbacks, never synchronously, so the
  // mount/filter effect below does not cascade a render.
  const fetchList = useCallback(
    () =>
      businessUpdateService
        .getList({page, limit: 10, status})
        .then(({data, total: itemsTotal}) => {
          setList(data);
          setTotal(itemsTotal || 0);
        })
        .catch((e: unknown) => {
          showErrorMessage(
            `Fetching product updates failed: ${getErrorMessage(e, 'unknown error')}`,
          );
        })
        .finally(() => {
          setLoadedQueryKey(queryKey);
        }),
    [page, status, queryKey],
  );

  useEffect(() => {
    // Fire-and-forget: fetchList catches and surfaces its own failures.
    fetchList().catch(() => {});
  }, [fetchList]);

  const handleStatusChange = useCallback(
    (nextStatus: BusinessUpdateStatus | undefined) => {
      setStatus(nextStatus);
      setPage(0);
    },
    [],
  );

  const handleDelete = useCallback(
    async (updateId: string) => {
      try {
        await businessUpdateService.deleteItem(updateId);
        showSuccessMessage('Update deleted');
        await fetchList();
      } catch (e) {
        showErrorMessage(
          `Deleting update failed: ${getErrorMessage(e, 'unknown error')}`,
        );
      }
    },
    [fetchList],
  );

  const handleUnpublish = useCallback(
    async (updateId: string) => {
      try {
        await businessUpdateService.unpublish(updateId);
        showSuccessMessage('Update unpublished');
        await fetchList();
      } catch (e) {
        showErrorMessage(
          `Unpublishing update failed: ${getErrorMessage(e, 'unknown error')}`,
        );
      }
    },
    [fetchList],
  );

  return {
    list,
    total,
    page,
    setPage,
    isLoading,
    status,
    handleStatusChange,
    handleDelete,
    handleUnpublish,
    refetch: fetchList,
  };
};

export default useProductUpdatesList;
